import { assertAuthoritativeInteger } from "./types.ts";

export type CanonicalSchema =
  | { readonly type: "integer" }
  | { readonly type: "string" }
  | { readonly type: "boolean" }
  | { readonly type: "null" }
  | { readonly type: "array"; readonly item: CanonicalSchema }
  | {
      readonly type: "entity-array";
      readonly idKey: string;
      readonly item: CanonicalRecordSchema;
    }
  | CanonicalRecordSchema;

export interface CanonicalRecordSchema {
  readonly type: "record";
  readonly fields: Readonly<Record<string, CanonicalSchema>>;
}

const FNV1A64_OFFSET = 14_695_981_039_346_656_037n;
const FNV1A64_PRIME = 1_099_511_628_211n;
const UINT64_MASK = 0xffff_ffff_ffff_ffffn;
const ASCII_RECORD_KEY = /^[\x20-\x7e]+$/u;

function asRecord(value: unknown): Record<string, unknown> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new TypeError("canonical record must be a plain record");
  }
  return value as Record<string, unknown>;
}

export function assertUnicodeScalarString(value: unknown, label: string): string {
  if (typeof value !== "string") {
    throw new TypeError(`${label} must be a string`);
  }
  for (let index = 0; index < value.length; index += 1) {
    const codeUnit = value.charCodeAt(index);
    if (codeUnit >= 0xd800 && codeUnit <= 0xdbff) {
      const next = value.charCodeAt(index + 1);
      if (index + 1 >= value.length || next < 0xdc00 || next > 0xdfff) {
        throw new TypeError(`${label} contains a lone surrogate`);
      }
      index += 1;
    } else if (codeUnit >= 0xdc00 && codeUnit <= 0xdfff) {
      throw new TypeError(`${label} contains a lone surrogate`);
    }
  }
  return value;
}

function encodeCanonical(value: unknown, schema: CanonicalSchema): string {
  switch (schema.type) {
    case "integer": {
      if (Object.is(value, -0)) {
        throw new RangeError("canonical integer cannot be negative zero");
      }
      return assertAuthoritativeInteger(value, "canonical integer").toString(10);
    }
    case "string": {
      return JSON.stringify(assertUnicodeScalarString(value, "canonical string"));
    }
    case "boolean": {
      if (typeof value !== "boolean") {
        throw new TypeError("canonical boolean must be a boolean");
      }
      return value ? "true" : "false";
    }
    case "null": {
      if (value !== null) {
        throw new TypeError("canonical null must be null");
      }
      return "null";
    }
    case "array": {
      if (!Array.isArray(value)) {
        throw new TypeError("canonical array must be an array");
      }
      return `[${value.map((entry) => encodeCanonical(entry, schema.item)).join(",")}]`;
    }
    case "entity-array": {
      if (!Array.isArray(value)) {
        throw new TypeError("canonical entity collection must be an array");
      }
      const sorted = value.map((entry) => ({
        entry,
        id: assertAuthoritativeInteger(
          asRecord(entry)[schema.idKey],
          "canonical entity id",
          1,
        ),
      }));
      sorted.sort((left, right) => (left.id < right.id ? -1 : left.id > right.id ? 1 : 0));
      for (let index = 1; index < sorted.length; index += 1) {
        if (sorted[index - 1]!.id === sorted[index]!.id) {
          throw new RangeError("duplicate canonical entity id");
        }
      }
      return `[${sorted.map(({ entry }) => encodeCanonical(entry, schema.item)).join(",")}]`;
    }
    case "record": {
      const record = asRecord(value);
      const knownKeys = Object.keys(schema.fields).sort();
      if (knownKeys.some((key) => !ASCII_RECORD_KEY.test(key))) {
        throw new TypeError("canonical record schema keys must be non-empty ASCII");
      }
      const inputKeys = Object.keys(record).sort();
      if (
        inputKeys.length !== knownKeys.length ||
        inputKeys.some((key, index) => key !== knownKeys[index])
      ) {
        throw new TypeError("canonical record keys do not match the schema");
      }
      return `{${knownKeys
        .map((key) => `${JSON.stringify(key)}:${encodeCanonical(record[key], schema.fields[key]!)}`)
        .join(",")}}`;
    }
  }
}

export function canonicalStringify(value: unknown, schema: CanonicalSchema): string {
  return encodeCanonical(value, schema);
}

export function canonicalUtf8(value: unknown, schema: CanonicalSchema): Uint8Array {
  return new TextEncoder().encode(canonicalStringify(value, schema));
}

export function fnv1a64(bytes: Uint8Array): string {
  let hash = FNV1A64_OFFSET;
  for (const byte of bytes) {
    hash ^= BigInt(byte);
    hash = (hash * FNV1A64_PRIME) & UINT64_MASK;
  }
  return hash.toString(16).padStart(16, "0");
}

export function hashCanonical(value: unknown, schema: CanonicalSchema): string {
  return fnv1a64(canonicalUtf8(value, schema));
}
